import React, { useEffect, useRef, useState } from "react";
import { Search, Crosshair, MapPin, LocateFixed } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import LeafletMap from "./LeafletMap";
import { requestLocation } from "../lib/geolocation";

// Fallback centre when nothing is set yet. Only ever used to POSITION the
// map — never written back as the picked location, so an admin who opens the
// form and never touches the map doesn't silently get these coordinates.
const DEFAULT_CENTER = { lat: 17.385044, lng: 78.486671 };

/**
 * Shape a raw Nominatim reverse-geocode result into the fields an apartment
 * actually stores. Every part is best-effort: Nominatim frequently knows the
 * road but not the house number, or the district but not the postcode, so
 * each value is independently optional and the caller keeps whatever it
 * already had for the ones that come back blank.
 */
function shapeReverseResult(j) {
  const a = (j && j.address) || {};
  return {
    // Street-level line, matching what AddressDialog builds from the same
    // payload so both forms describe a place the same way.
    address: [a.house_number, a.road || a.pedestrian || a.neighbourhood]
      .filter(Boolean).join(" ")
      || [a.suburb, a.city_district].filter(Boolean).join(", "),
    city: a.city || a.town || a.village || a.municipality || a.county || "",
    pincode: a.postcode || "",
    display: (j && j.display_name) || "",
  };
}

/**
 * Search + drag-a-pin location picker.
 *
 * Controlled: `lat`/`lng` may be null (nothing picked yet) and `onChange`
 * fires with real numbers the first time the admin searches, taps the map,
 * drags the pin, or uses their current location.
 *
 * Geocoding goes through the backend proxy (backend/geocoding.py), not
 * Nominatim directly — its usage policy caps the public server at ~1 req/sec
 * and requires an identifying User-Agent, neither enforceable from many
 * independent browsers. Calling it client-side previously got the shared IP
 * blocked outright.
 */
export default function MapPicker({
  lat, lng, onChange,
  onResolve,
  label = "Location",
  hint = "Search, then drag the pin to the exact spot.",
  testIdPrefix = "map-picker",
}) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [resolving, setResolving] = useState(false);

  // Latest callback without making it an effect dependency — otherwise a
  // parent that re-creates the handler each render would re-geocode forever.
  const onResolveRef = useRef(onResolve);
  onResolveRef.current = onResolve;

  const hasPin = lat != null && lng != null && lat !== "" && lng !== "";

  // Whenever the pin lands somewhere real, look up what's there and hand the
  // details to the parent. Covers every way the pin can move — search result,
  // map tap, drag, "use my location" — in one place, so no path silently
  // skips auto-fill.
  //
  // Debounced: a drag fires many intermediate positions, and the backend
  // proxy throttles Nominatim to ~1 req/sec (geocoding.py). Superseded
  // lookups are dropped rather than applied out of order.
  useEffect(() => {
    if (!onResolveRef.current || !hasPin) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      setResolving(true);
      try {
        const { data } = await api.get("/api/public/geocode/reverse", {
          params: { lat: Number(lat), lon: Number(lng) },
        });
        if (!cancelled) onResolveRef.current?.(shapeReverseResult(data));
      } catch {
        // Silent: the pin is still valid, we just couldn't name the place.
        // The admin fills the remaining fields by hand, which the form
        // already supports.
      } finally {
        if (!cancelled) setResolving(false);
      }
    }, 600);
    return () => { cancelled = true; clearTimeout(t); };
  }, [lat, lng, hasPin]);
  const pinLat = hasPin ? Number(lat) : DEFAULT_CENTER.lat;
  const pinLng = hasPin ? Number(lng) : DEFAULT_CENTER.lng;

  const search = async () => {
    const q = query.trim();
    if (!q) return;
    setSearching(true);
    try {
      const { data } = await api.get("/api/public/geocode/search", { params: { q } });
      const rows = data.results || [];
      setResults(rows);
      if (rows.length === 0) toast.error("No places matched that search");
    } catch {
      // A real failure must be distinguishable from "no results" — otherwise
      // the admin retypes the query forever against a down geocoder.
      toast.error("Couldn't reach the map search. Drop the pin manually instead.");
    } finally { setSearching(false); }
  };

  const pick = (r) => {
    const nlat = parseFloat(r.lat);
    const nlng = parseFloat(r.lon);
    if (Number.isNaN(nlat) || Number.isNaN(nlng)) return;
    onChange(nlat, nlng);
    setResults([]);
    setQuery(r.display_name || query);
  };

  const useCurrent = async () => {
    setLocating(true);
    try {
      const r = await requestLocation();
      if (r.status === "granted" && r.coords) {
        onChange(r.coords.lat, r.coords.lng);
        toast.success("Location set from your device");
      } else {
        toast.error("Couldn't get your location — search or drop the pin instead.");
      }
    } catch {
      toast.error("Couldn't get your location — search or drop the pin instead.");
    } finally { setLocating(false); }
  };

  return (
    <div className="space-y-2" data-testid={testIdPrefix}>
      <label className="text-xs uppercase tracking-wider font-bold text-neutral-500 block">{label}</label>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"/>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); search(); } }}
          placeholder="Search apartment, road or landmark…"
          className="w-full h-11 pl-9 pr-20 rounded-2xl border border-neutral-200 text-sm"
          data-testid={`${testIdPrefix}-search-input`}
        />
        <button type="button" onClick={search} disabled={searching}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 h-8 px-3 rounded-xl bg-brand-sky text-white text-xs font-bold disabled:opacity-50"
                data-testid={`${testIdPrefix}-search-btn`}>
          {searching ? "…" : "Search"}
        </button>
        {results.length > 0 && (
          <div className="absolute z-30 mt-1 w-full bg-white border border-neutral-200 rounded-2xl shadow-lg max-h-56 overflow-y-auto"
               data-testid={`${testIdPrefix}-results`}>
            {results.map((r) => (
              <button key={r.place_id} type="button" onClick={() => pick(r)}
                      className="w-full text-left p-3 hover:bg-neutral-50 border-b border-neutral-100 text-sm last:border-b-0"
                      data-testid={`${testIdPrefix}-result-${r.place_id}`}>
                {r.display_name}
              </button>
            ))}
          </div>
        )}
      </div>

      <LeafletMap lat={pinLat} lng={pinLng} onChange={onChange}
                  testId={`${testIdPrefix}-map`}/>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[11px] text-neutral-500 inline-flex items-center gap-1.5">
          <Crosshair className="w-3 h-3"/> {hint}
        </div>
        <button type="button" onClick={useCurrent} disabled={locating}
                className="h-8 px-3 rounded-xl border border-neutral-200 text-[11px] font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                data-testid={`${testIdPrefix}-use-current`}>
          <LocateFixed className={`w-3 h-3 ${locating ? "animate-pulse" : ""}`}/>
          {locating ? "Locating…" : "Use my location"}
        </button>
      </div>

      {/* Says plainly whether a location is actually stored. Without this the
          map always looks "set" because a pin is always drawn — at the
          fallback centre when nothing has been picked. */}
      <div className={`text-[11px] inline-flex items-center gap-1.5 ${hasPin ? "text-brand-leaf" : "text-neutral-500"}`}
           data-testid={`${testIdPrefix}-status`}>
        <MapPin className="w-3 h-3"/>
        {!hasPin
          ? "No location set yet — search or tap the map."
          : resolving
            ? "Looking up address details…"
            : <>Location set · <span className="font-mono">{Number(lat).toFixed(6)}, {Number(lng).toFixed(6)}</span></>}
      </div>
    </div>
  );
}
